/**
 * §18.7 — "Liabilities and deposits: what is owed per tax, when it is due, the
 * $2,500 quarterly limit (§18.5), and which bank payments cleared it."
 * Payroll stage 4's deposit tracker.
 *
 * THE PLAN IS THE SERVER'S. GET /payroll/deposits groups what the books
 * accrued on 2410 to 2450 into the deposits Publication 15 requires and lays
 * each payment against the oldest liability on its account
 * (apps/api/src/payroll/deposits.ts). This screen dates, sums and allocates
 * nothing; it shows the plan, and a run's own notice reads the same one.
 *
 * WHAT IS OWED COMES FIRST, one card each, because that is the question the
 * screen is opened to answer. A card names the pay it is for, its due date and
 * why, the split across the accounts it clears, and the runs and entries behind
 * it -- the drill-down §18.7 asks for.
 *
 * A SPLIT THAT DISAGREES IS SAID, NOT HIDDEN. A payment applied to the wrong
 * account shows here as one account owing and another paid more than it
 * accrued, and the account table names the surplus. The screen never presents a
 * balanced split the books do not hold.
 *
 * THE BANK'S DATE, NOT A PENALTY. A paid deposit whose bank date is after its
 * due date is marked, with a note saying why the bank's date may not be the
 * IRS's. The application does not know which service made the payment.
 *
 * THE BANK'S DEBIT IS CODED HERE, NEVER A SECOND ENTRY (§18.6). A card lists
 * the uncoded Mercury debits of exactly what it owes, each with the run page's
 * own "Use This Debit": choosing one codes that transaction as the deposit's
 * payment, split as the Outstanding column shows. A debit of another amount to
 * a payee this tax was paid to is named, and sent to the editor -- how a
 * part-payment splits between the accounts is the operator's to decide.
 *
 * Nothing here files or pays anything: the payment was made at the bank, and
 * this records it.
 *
 * TOTAL ROWS, THE SERVER'S TOTALS (2026-09-23). A deposit of more than one
 * account totals its split, and the Outstanding total is the amount to pay at
 * EFTPS; the liability accounts total too, and their balance is the check on
 * the deposits above. Each total is a figure the plan already carries, so the
 * screen still adds nothing up. One line is its own total and gets none; the
 * quarters get none, since the $2,500 test is per quarter and no rule reads
 * their sum.
 */

import { AccessNotice } from '../components/AccessNotice.js';
import { useCan } from '../auth/AuthProvider.js';
import { useCallback, useEffect, useId, useState } from 'react';
import { Link } from 'react-router-dom';
import { InfoNote } from '../components/InfoNote.js';
import { Toast } from '../components/Toast.js';
import {
  ApiError,
  api,
  type DepositObligation,
  type DepositStatus,
  type DepositTracker,
  type LiabilityRole,
  type TaxDebitCandidate,
} from '../api.js';
import { formatCents } from '../lib/money.js';
import { settingsTab } from '../lib/settings-tabs.js';

/** What a deposit is called: the tax, as the server names it, and the period after it. */
function titleOf(obligation: DepositObligation): string {
  return `${obligation.title}: ${obligation.label}`;
}

/** A date inside running text, kept on one line: "2026-" and "07-17" are not two things. */
function Day({ date }: { date: string }) {
  return <span className="date-cell">{date}</span>;
}

const STATUS_LABEL: Record<DepositStatus, string> = {
  paid: 'Paid',
  due: 'Due',
  overdue: 'Overdue',
  undated: 'No Date',
};

const STATUS_TAG: Record<DepositStatus, string> = {
  paid: 'tag-known',
  due: 'tag-due',
  overdue: 'tag-overdue',
  undated: 'tag-due',
};

const REQUIREMENT_LABEL: Record<DepositTracker['quarters'][number]['requirement'], string> = {
  at_or_above: 'At or Above $2,500',
  under_so_far: 'Under So Far',
  ended_under: 'Ended Under — With the Return',
};

/** Where an account is missing from the chart, its role still says which it was. */
const ROLE_NAME: Record<LiabilityRole, string> = {
  federal_income_tax_withheld: 'Federal Income Tax Withheld',
  social_security_tax_payable: 'Social Security Tax Payable',
  medicare_tax_payable: 'Medicare Tax Payable',
  futa_tax_payable: 'FUTA Tax Payable',
  texas_unemployment_tax_payable: 'Texas Unemployment Tax Payable',
};

function accountName(tracker: DepositTracker, role: LiabilityRole): string {
  const account = tracker.accountsByRole[role];
  return account ? `${account.code} ${account.name}` : ROLE_NAME[role];
}

/** One line per entry, however many accounts it touched. */
function byEntry<T extends { headerId: string; cents: number }>(items: readonly T[]): Array<T & { totalCents: number }> {
  const entries = new Map<string, T & { totalCents: number }>();
  for (const item of items) {
    const seen = entries.get(item.headerId);
    if (seen) seen.totalCents += item.cents;
    else entries.set(item.headerId, { ...item, totalCents: item.cents });
  }
  return [...entries.values()];
}

export function DepositsPage() {
  const mayRun = useCan()('run_payroll');
  const [tracker, setTracker] = useState<DepositTracker | null>(null);
  const [loadError, setLoadError] = useState<string | null>(null);
  const [historyOpen, setHistoryOpen] = useState(false);
  const [busy, setBusy] = useState(false);
  const [notice, setNotice] = useState<string | null>(null);
  const [postRefusal, setPostRefusal] = useState<string | null>(null);
  /** A refusal belongs to the card whose debit was refused. */
  const [refusal, setRefusal] = useState<{ key: string; message: string } | null>(null);

  const load = useCallback(async () => {
    try {
      const result = await api.getDepositTracker();
      setTracker(result.tracker);
      setLoadError(null);
    } catch (caught) {
      setLoadError(caught instanceof ApiError ? caught.message : 'Could not read the deposits.');
    }
  }, []);

  useEffect(() => {
    void load();
  }, [load]);

  async function applyDebit(obligation: DepositObligation, debit: TaxDebitCandidate) {
    setBusy(true);
    setRefusal(null);
    setPostRefusal(null);
    try {
      const coded = await api.codeTaxPayment({ obligationKey: obligation.key, bankDebitHeaderId: debit.headerId });
      // The answer is the whole tracker, so every section reads the same payment.
      setTracker(coded.tracker);
      setPostRefusal(coded.postRefusal);
      setNotice(`The ${formatCents(-debit.amountCents)} debit of ${debit.paidOn} now pays ${titleOf(obligation)}`);
    } catch (caught) {
      setRefusal({
        key: obligation.key,
        message: caught instanceof ApiError ? caught.message : 'Could not use that bank debit.',
      });
    } finally {
      setBusy(false);
    }
  }

  if (loadError) {
    return (
      <section className="deposits">
        <p>
          <Link to="/payroll">Back to Payroll</Link>
        </p>
        <h1>Payroll Tax Deposits</h1>
        <p role="alert" className="error">
          {loadError}
        </p>
      </section>
    );
  }
  if (!tracker) return <p className="muted">Loading…</p>;

  const owed = tracker.obligations.filter((obligation) => obligation.status !== 'paid');
  // The latest first: the history is read backwards from now.
  const paid = tracker.obligations.filter((obligation) => obligation.status === 'paid').reverse();
  const surplus = tracker.accounts.filter((account) => account.unappliedCents > 0);

  return (
    <section className="deposits">
      <p>
        <Link to="/payroll">Back to Payroll</Link>
      </p>
      <h1>Payroll Tax Deposits</h1>
      {notice && <Toast message={notice} variant="ok" onDismiss={() => setNotice(null)} />}
      <AccessNotice capability="run_payroll" what="deposits" />
      <p className="muted">
        What the books say is owed for each payroll tax, when it is due, and the bank payments that cleared it, as of{' '}
        {tracker.today}. Nothing here files or pays anything.
      </p>
      {postRefusal && <p className="notice notice-warn">{postRefusal}</p>}

      <section className="settings-section" aria-labelledby="deposits-owed">
        <h2 id="deposits-owed">What Is Owed</h2>
        {owed.length === 0 ? (
          <p className="muted">Nothing: every payroll tax the books accrued has been paid.</p>
        ) : (
          owed.map((obligation) => (
            <OwedDeposit
              key={obligation.key}
              obligation={obligation}
              tracker={tracker}
              busy={busy}
              refusal={refusal?.key === obligation.key ? refusal.message : null}
              // Using a debit records the payment, which is run_payroll's.
              onUse={mayRun ? (debit) => void applyDebit(obligation, debit) : undefined}
            />
          ))
        )}
      </section>

      <section className="settings-section" aria-labelledby="deposits-accounts">
        <h2 id="deposits-accounts">Liability Accounts</h2>
        {tracker.accounts.length === 0 ? (
          <p className="muted">No payroll tax has been accrued or paid on these accounts yet.</p>
        ) : (
          <table className="lines" aria-label="Liability Accounts">
            <thead>
              <tr>
                <th>Account</th>
                <th className="num">Accrued</th>
                <th className="num">Paid</th>
                <th className="num">Balance</th>
              </tr>
            </thead>
            <tbody>
              {tracker.accounts.map((account) => (
                <tr key={account.role}>
                  <td>{accountName(tracker, account.role)}</td>
                  <td className="num">{formatCents(account.accruedCents)}</td>
                  <td className="num">{formatCents(account.paidCents)}</td>
                  <td className="num">{formatCents(account.balanceCents)}</td>
                </tr>
              ))}
            </tbody>
            {tracker.accounts.length > 1 && (
              <tfoot>
                <tr>
                  <th scope="row">Total</th>
                  <td className="num">{formatCents(tracker.accountTotals.accruedCents)}</td>
                  <td className="num">{formatCents(tracker.accountTotals.paidCents)}</td>
                  <td className="num">{formatCents(tracker.accountTotals.balanceCents)}</td>
                </tr>
              </tfoot>
            )}
          </table>
        )}
        {/*
          The one place a payment the books cannot place is named. It is the
          signature of a split that disagrees with what was accrued -- the
          IRS sees one deposit, the books see one account over and another
          under -- or of a payment for a liability the books never accrued.
        */}
        {surplus.map((account) => (
          <p key={account.role} className="notice notice-warn">
            {accountName(tracker, account.role)} has been paid {formatCents(account.unappliedCents)} more than was ever
            accrued on it. A payment's split across the accounts differs from what the pay accrued, or it paid something
            the books do not hold. Correct the split in the payment's entry.
          </p>
        ))}
      </section>

      {tracker.quarters.length > 0 && (
        <section className="settings-section" aria-labelledby="deposits-quarters">
          <h2 id="deposits-quarters">
            Form 941 Liability by Quarter{' '}
            <InfoNote label="What the $2,500 test decides">
              A quarter at or above $2,500 is deposited on the schedule. One still under it so far keeps its deposit dates,
              because Publication 15 says to deposit unless you are sure the quarter will end under; one that ended under
              may be paid with the return. Publication 15 also allows the return when the previous quarter was under $2,500,
              and this application does not apply that.
            </InfoNote>
          </h2>
          <table className="lines" aria-label="Form 941 Liability by Quarter">
            <thead>
              <tr>
                <th>Quarter</th>
                <th>$2,500 Test</th>
                <th className="num">Liability</th>
              </tr>
            </thead>
            <tbody>
              {tracker.quarters.map((quarter) => (
                <tr key={quarter.quarter}>
                  <td>{quarter.quarter}</td>
                  <td>
                    {REQUIREMENT_LABEL[quarter.requirement]}
                    {quarter.nextDay && ' · a $100,000 day'}
                  </td>
                  <td className="num">{formatCents(quarter.federalLiabilityCents)}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </section>
      )}

      {paid.length > 0 && (
        <section className="deposit-history" aria-label="Paid Deposits">
          <button
            type="button"
            className="home-health-toggle"
            aria-expanded={historyOpen}
            onClick={() => setHistoryOpen((open) => !open)}
          >
            <span>
              <strong>Paid</strong>{' '}
              <span className="muted">
                {paid.length} {paid.length === 1 ? 'deposit' : 'deposits'}, the latest paid <Day date={paid[0]!.paidOn ?? ''} />
              </span>
            </span>
            <span className="home-health-chevron" aria-hidden="true">
              {historyOpen ? '▲' : '▼'}
            </span>
          </button>
          {historyOpen && (
            <table className="lines" aria-label="Paid Deposits">
              <thead>
                <tr>
                  <th>Due</th>
                  <th>For</th>
                  <th className="num">Amount</th>
                  <th>Paid By</th>
                </tr>
              </thead>
              <tbody>
                {paid.map((obligation) => (
                  <tr key={obligation.key}>
                    <td className="date-cell">{obligation.dueDate}</td>
                    <td>{titleOf(obligation)}</td>
                    <td className="num">{formatCents(obligation.owedCents)}</td>
                    <td>
                      <Payments obligation={obligation} />
                      {obligation.dueDate !== null && obligation.paidOn !== null && obligation.paidOn > obligation.dueDate && (
                        <p className="muted">
                          The bank's date is after the due date.{' '}
                          <InfoNote label="What the bank's date means" anchored>
                            It is the day Mercury shows the debit. A payment scheduled through IRS Direct Pay counts as
                            made on the day it was scheduled for, which can be a day or two before the bank's date;
                            through EFTPS, on its settlement date. The payment's confirmation says which.
                          </InfoNote>
                        </p>
                      )}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          )}
        </section>
      )}
    </section>
  );
}

function OwedDeposit({
  obligation,
  tracker,
  busy,
  refusal,
  onUse,
}: {
  obligation: DepositObligation;
  tracker: DepositTracker;
  busy: boolean;
  refusal: string | null;
  /** Absent for a session that may not record a payment: the debits are listed, not offered. */
  onUse?: (debit: TaxDebitCandidate) => void;
}) {
  /*
   * NOT built from the key. "texas:Q1 2026" has a space in it, and
   * aria-labelledby takes a SPACE-SEPARATED list of ids, so every Texas and
   * FUTA card was labelled by two ids that do not exist and had no name at all.
   * A test naming a quarter's card by its title found it on 2026-09-22.
   */
  const heading = useId();
  return (
    <article className="deposit-card" aria-labelledby={heading}>
      <h3 id={heading}>{titleOf(obligation)}</h3>
      <p>
        <span className={`tag ${STATUS_TAG[obligation.status]}`}>{STATUS_LABEL[obligation.status]}</span>{' '}
        {obligation.dueDate === null ? (
          <>
            No due date: no deposit schedule is recorded for this pay.{' '}
            <Link to={settingsTab('payroll')}>Record the Deposit Schedule</Link>
          </>
        ) : (
          <>
            Due <strong className="deadline-date">{obligation.dueDate}</strong>
            {obligation.movedFrom && <span className="muted"> (was {obligation.movedFrom})</span>}
          </>
        )}{' '}
        · <strong>{formatCents(obligation.outstandingCents)}</strong> outstanding
        {obligation.paidCents > 0 && <span className="muted"> of {formatCents(obligation.owedCents)}</span>}
      </p>
      <p className="muted">{obligation.because}</p>

      {/* The whole title: a FUTA quarter and a Texas quarter share a label. */}
      <table className="lines" aria-label={`${titleOf(obligation)} by Account`}>
        <thead>
          <tr>
            <th>Account</th>
            <th className="num">Owed</th>
            <th className="num">Paid</th>
            <th className="num">Outstanding</th>
          </tr>
        </thead>
        <tbody>
          {obligation.lines.map((line) => (
            <tr key={line.role}>
              <td>{accountName(tracker, line.role)}</td>
              <td className="num">{formatCents(line.owedCents)}</td>
              <td className="num">{formatCents(line.paidCents)}</td>
              <td className="num">{formatCents(line.owedCents - line.paidCents)}</td>
            </tr>
          ))}
        </tbody>
        {obligation.lines.length > 1 && (
          <tfoot>
            <tr>
              <th scope="row">Total</th>
              <td className="num">{formatCents(obligation.owedCents)}</td>
              <td className="num">{formatCents(obligation.paidCents)}</td>
              <td className="num">{formatCents(obligation.outstandingCents)}</td>
            </tr>
          </tfoot>
        )}
      </table>

      {obligation.bankDebits.length > 0 && (
        <>
          <p className="muted">
            Mercury holds an uncategorized debit of exactly what is owed. Using it categorizes that transaction as this
            deposit's payment, split as the Outstanding column shows, so the payment is booked once.
          </p>
          <ul className="history-list" aria-label="Bank Debits">
            {obligation.bankDebits.map((debit) => (
              <li key={debit.headerId}>
                <p>
                  <Day date={debit.paidOn} /> · {debit.counterparty ?? debit.bankMemo ?? 'no description'} ·{' '}
                  {formatCents(-debit.amountCents)} · {debit.accountCode} {debit.accountName}
                </p>
                {onUse && (
                  <button type="button" disabled={busy} onClick={() => onUse(debit)}>
                    Use This Debit
                  </button>
                )}
              </li>
            ))}
          </ul>
        </>
      )}
      {refusal && (
        <p role="alert" className="error">
          {refusal}
        </p>
      )}
      {obligation.otherDebits.length > 0 && (
        <>
          <p className="muted">
            Also uncategorized, to a payee this tax has been paid to, and not the{' '}
            {formatCents(obligation.outstandingCents)} owed. How a payment of another amount splits between the accounts
            is yours to decide, so it is categorized in the editor, not here.
          </p>
          <ul className="history-list" aria-label="Other Payments">
            {obligation.otherDebits.map((debit) => (
              <li key={debit.headerId}>
                <Day date={debit.paidOn} /> · {debit.counterparty ?? 'no description'} · {formatCents(-debit.amountCents)}{' '}
                · <Link to={`/journals/${debit.headerId}`}>Categorize It in the Editor</Link>
              </li>
            ))}
          </ul>
        </>
      )}

      <p className="muted">
        Accrued by{' '}
        {byEntry(obligation.accruals).map((accrual, index) => (
          <span key={accrual.headerId}>
            {index > 0 && ', '}
            {accrual.runId !== null ? (
              <Link to={`/payroll/runs/${accrual.runId}`}>
                the pay run of <Day date={accrual.payDate} />
              </Link>
            ) : (
              <Link to={`/journals/${accrual.headerId}`}>
                the entry of <Day date={accrual.payDate} />
              </Link>
            )}
            {!accrual.posted && ' (a draft)'}
          </span>
        ))}
        .
      </p>
      {obligation.payments.length > 0 && (
        <p className="muted">
          Paid so far by <Payments obligation={obligation} />
        </p>
      )}
    </article>
  );
}

function Payments({ obligation }: { obligation: DepositObligation }) {
  return (
    <>
      {byEntry(obligation.payments).map((payment, index) => (
        <span key={payment.headerId}>
          {index > 0 && ', '}
          <Link to={`/journals/${payment.headerId}`}>
            {payment.counterparty ?? 'a payment'} on <Day date={payment.paidOn} />
          </Link>{' '}
          ({formatCents(payment.totalCents)}
          {!payment.posted && ', a draft'})
        </span>
      ))}
    </>
  );
}
